<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import CustomFullCalendar from "@/components/CustomFullCalendar";
import i18n from "@/i18n";
import { SHARED } from "@/constants";
import { mapState } from "vuex";
import { USER_METADATA_MODULE } from "@/state/modules/userMetadata";
import { userMetadataMethods } from "@/state/helpers";

const APPROVED_COLOR = "#34c38f"; // green
const PENDING_COLOR = "#f1b44c"; // amber

export default {
  page: { title: i18n.t("label.listingX", [i18n.t("module.checkIn")]) },
  components: { Layout, PageHeader, CustomFullCalendar },
  data() {
    return {
      title: i18n.t("label.listingX", [i18n.t("module.checkIn")]),
      breadcrumbItems: [
        { text: i18n.t("module.admin"), href: "/" },
        { text: i18n.t("module.checkIn"), active: true },
      ],
      filter: "",
      currentPage: 1,
      perPage: SHARED.DEFAULT_PAGE_SIZE,
      fields: [
        { key: "userName", sortable: true, label: i18n.t("label.user") || "User" },
        { key: "name", sortable: true, label: i18n.t("label.name") },
        { key: "isOwnRecord", sortable: true, label: "Own Record" },
        { key: "isApproved", sortable: true, label: i18n.t("label.status") },
        { key: "approvedBy", sortable: false, label: "Approved By" },
        { key: "createdAt", sortable: true, label: i18n.t("label.createdAt") },
        { key: "action", sortable: false, label: i18n.t("label.action") },
      ],
      searchCriteria: {},
      viewMode: "table", // 'table' | 'calendar'
      calendarRange: null, // { start, end } in Y-m-d, from CustomFullCalendar 'filter' emit
      calendarIndicator: [
        { color: APPROVED_COLOR, name: i18n.t("label.approved") },
        { color: PENDING_COLOR, name: i18n.t("label.pending") },
      ],
      viewing: null, // record shown in the calendar detail modal
    };
  },
  computed: {
    ...mapState(USER_METADATA_MODULE, ["status"]),
    tableRows() {
      return this.$store.state.userMetadata.list.data;
    },
    pagination() {
      return this.$store.state.userMetadata.list.pagination;
    },
    calendarEvents() {
      return (this.$store.state.userMetadata.list.data || [])
        .filter((row) => row.createdAtRaw)
        .map((row) => {
          const approved = !!row.isApproved;
          const color = approved ? APPROVED_COLOR : PENDING_COLOR;
          const title = `${row.userName || ""} ${row.name || ""}`.trim() || "Check-in";
          return {
            id: row.uuid || row.id,
            title,
            start: row.createdAtRaw,
            backgroundColor: color,
            borderColor: color,
            textColor: "#ffffff",
            extendedProps: { record: row },
          };
        });
    },
  },
  created() {
    this.searchCriteria = {
      pageNumber: SHARED.DEFAULT_PAGE_NUMBER,
      pageSize: SHARED.DEFAULT_PAGE_SIZE,
      searchArray: [],
    };
    this.getList(this.searchCriteria);
  },
  methods: {
    ...userMetadataMethods,
    getList(filter) {
      this.getAllUserMetadata({ filter });
      this.searchCriteria = filter;
    },
    pageNavigation(pageNumber) {
      this.searchCriteria.pageNumber = pageNumber;
      this.getList(this.searchCriteria);
    },
    searchListing() {
      this.searchCriteria.pageNumber = SHARED.DEFAULT_PAGE_NUMBER;
      this.searchCriteria.searchArray = [this.filter];
      this.getList(this.searchCriteria);
    },
    approve(row) {
      this.approveUserMetadata({ id: row.id, filter: this.searchCriteria }).then(() => {
        if (this.viewMode === "calendar") {
          // keep the calendar in sync after approving from the detail modal
          this.reloadCalendar();
          if (this.viewing) this.viewing.isApproved = true;
        }
      });
    },
    setViewMode(mode) {
      if (this.viewMode === mode) return;
      this.viewMode = mode;
      if (mode === "table") {
        // restore the paginated table dataset
        this.getList(this.searchCriteria);
      } else if (this.calendarRange) {
        this.reloadCalendar();
      }
    },
    // CustomFullCalendar emits 'filter' with { start, end } in Y-m-d on view/date-range change
    onCalendarRangeChange(payload) {
      this.calendarRange = { start: payload.start, end: payload.end };
      this.reloadCalendar();
    },
    reloadCalendar() {
      if (!this.calendarRange) return;
      // Admin view: load all users' check-ins within the visible range (no is_own_record).
      this.getAllUserMetadata({
        filter: {
          pageNumber: 1,
          pageSize: 1000,
          searchArray: [],
          dateFrom: this.calendarRange.start,
          dateTo: this.calendarRange.end,
        },
      });
    },
    // CustomFullCalendar emits 'leftClick' with { id, details } on event click
    onCalendarEventClick(payload) {
      const record = payload?.details?.record;
      if (record) {
        this.viewing = record;
        this.$bvModal.show("checkInDetailModal");
      }
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbItems" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mt-2 mb-3 align-items-center">
              <div class="col-sm-12 col-md-6">
                <label class="d-inline-flex align-items-center" v-if="viewMode === 'table'">
                  {{ $t('label.search') }}
                  <b-form-input v-model="filter" type="search" class="form-control form-control-sm ml-2" @change="searchListing"></b-form-input>
                </label>
              </div>
              <div class="col-sm-12 col-md-6 text-md-right">
                <b-button-group size="sm">
                  <b-button :variant="viewMode === 'table' ? 'primary' : 'outline-primary'" @click="setViewMode('table')">
                    <i class="mdi mdi-table mr-1"></i>{{ $t('label.tableView') }}
                  </b-button>
                  <b-button :variant="viewMode === 'calendar' ? 'primary' : 'outline-primary'" @click="setViewMode('calendar')">
                    <i class="mdi mdi-calendar mr-1"></i>{{ $t('label.calendarView') }}
                  </b-button>
                </b-button-group>
              </div>
            </div>

            <!-- TABLE VIEW -->
            <div v-show="viewMode === 'table'">
              <div class="table-responsive mb-0">
                <b-table :items="tableRows" :fields="fields" responsive="sm" :per-page="perPage">
                  <template #cell(isOwnRecord)="data">
                    <b-badge variant="info" v-if="tableRows[data.index].isOwnRecord">{{ $t('label.yes') }}</b-badge>
                    <span v-else class="text-muted">—</span>
                  </template>
                  <template #cell(isApproved)="data">
                    <b-badge variant="success" v-if="tableRows[data.index].isApproved">Approved</b-badge>
                    <b-badge variant="warning" v-else>Pending</b-badge>
                  </template>
                  <template #cell(action)="data">
                    <a href="javascript:void(0);" v-if="!tableRows[data.index].isApproved" @click="approve(tableRows[data.index])" class="text-success" v-b-tooltip.hover :title="$t('label.approve')">
                      <i class="mdi mdi-check-bold font-size-18"></i>
                    </a>
                  </template>
                </b-table>
              </div>
              <div class="row">
                <div class="col">
                  <div class="dataTables_paginate paging_simple_numbers float-right">
                    <ul class="pagination pagination-rounded mb-0">
                      <b-pagination v-model="currentPage" :total-rows="pagination.total" :per-page="perPage" @change="pageNavigation"></b-pagination>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <!-- CALENDAR VIEW -->
            <div v-if="viewMode === 'calendar'">
              <CustomFullCalendar
                initial-view="dayGridMonth"
                calendar-modes="standard"
                :events-data="calendarEvents"
                :indicator="calendarIndicator"
                @filter="onCalendarRangeChange"
                @leftClick="onCalendarEventClick"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- CALENDAR EVENT DETAIL / APPROVE MODAL -->
    <b-modal id="checkInDetailModal" :title="viewing ? (viewing.name || $t('module.checkIn')) : ''" hide-footer scrollable>
      <div v-if="viewing">
        <dl class="row mb-3">
          <dt class="col-sm-4">{{ $t('label.user') }}</dt>
          <dd class="col-sm-8">{{ viewing.userName || '—' }}</dd>
          <dt class="col-sm-4">{{ $t('label.name') }}</dt>
          <dd class="col-sm-8">{{ viewing.name || '—' }}</dd>
          <dt class="col-sm-4">{{ $t('label.createdAt') }}</dt>
          <dd class="col-sm-8">{{ viewing.createdAt }}</dd>
          <dt class="col-sm-4">{{ $t('label.debtorCode') }}</dt>
          <dd class="col-sm-8">
            <b-badge variant="light" v-if="viewing.debtorCode">{{ viewing.debtorCode }}</b-badge>
            <span v-else class="text-muted">—</span>
          </dd>
          <dt class="col-sm-4">{{ $t('label.location') }}</dt>
          <dd class="col-sm-8">
            <span v-if="viewing.latitude != null && viewing.longitude != null">{{ viewing.latitude }}, {{ viewing.longitude }}</span>
            <span v-else class="text-muted">—</span>
          </dd>
          <dt class="col-sm-4">{{ $t('label.status') }}</dt>
          <dd class="col-sm-8">
            <b-badge variant="success" v-if="viewing.isApproved">Approved</b-badge>
            <b-badge variant="warning" v-else>Pending</b-badge>
          </dd>
        </dl>
        <div v-if="viewing.image" class="mb-3 text-center">
          <img :src="viewing.image" class="img-fluid rounded" :alt="$t('label.image')" style="max-height: 300px;" />
        </div>
        <div class="text-right" v-if="!viewing.isApproved">
          <b-button variant="success" size="sm" @click="approve(viewing)">
            <i class="mdi mdi-check-bold mr-1"></i>{{ $t('label.approve') }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </Layout>
</template>
